import { CancelConfirmationModal, Spinner, makeConcealable } from '@ami/react/core';
import { OrderInput } from '@ami/react/shared';
import _ from 'lodash';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import React from 'react';
import { NotificationManager } from 'react-notifications';
import { withRouter } from 'react-router-dom';
import { SharedDocument } from '../../documents/sharedDocument';
import { ProductConfiguration } from '../../products/productConfiguration';
import { getQuoteProductLeadTime } from '../../quotes/lead-times/leadTimeCalculator';
import {
  QuoteProductDto,
  couplerDecision
} from '../../quotes/otherQuoteEntities';
import { Quote } from '../../quotes/quote';
import { QuoteProductConfiguration } from '../../quotes/quoteProductConfiguration';
import { quoteWizardMode } from '../../quotes/quoteWizardMode';
import CreateQuoteStep3Component from './CreateQuoteStep3Component';
import quotePricingService from '../../quotes/quotePricingService';
import quoteProductLeadTimeService from '../../quotes/quoteProductLeadTimeService';

class CreateQuoteStep3ContainerComponent extends React.Component {
  static propTypes = {
    productConfiguration: PropTypes.instanceOf(ProductConfiguration).isRequired,
    quoteProductConfiguration: PropTypes.instanceOf(QuoteProductConfiguration)
      .isRequired,
    submitting: PropTypes.bool.isRequired,
    onAddProduct: PropTypes.func.isRequired,
    onUpdateProduct: PropTypes.func.isRequired,
    onContinue: PropTypes.func.isRequired,
    markSelectionInProgress: PropTypes.func.isRequired,
    quote: PropTypes.instanceOf(Quote).isRequired,
    contact: PropTypes.instanceOf(Object),
    preparingForReview: PropTypes.bool.isRequired,
    mode: PropTypes.oneOf(
      Object.keys(quoteWizardMode).map((item) => quoteWizardMode[item])
    ).isRequired,
    onloadSharedDocuments: PropTypes.func.isRequired,
    onQuoteUpdated: PropTypes.func.isRequired,
    order: PropTypes.instanceOf(OrderInput)
  };

  state = {
    quoteProductDto: null,
    showCancelConfirmationModal: false,
    isEditProductMode: false,
    hasAgreedStockTerms: false,
    documents: null,
    selectedQuoteProductType: 1,
    showPricingSpinner: false,
    showLeadTimeCalculationSpinner: false,
    forceResetLeadTimeOverrides: false
  };

  async componentDidMount() {
    const { quoteProductConfiguration } = this.props;
    const defaultQuoteProduct = this.getDefaultQuoteProduct(quoteProductConfiguration);
    this.setState({
      quoteProductDto: defaultQuoteProduct,
      isEditProductMode: !!this.getQueryObject().quoteProductId,
    });
  }

  getDefaultQuoteProduct = (quoteProductConfiguration) => {
    let quoteProduct;
    const queryObject = this.getQueryObject();
    const isEditProductMode = !!queryObject.quoteProductId;

    if (isEditProductMode) {
      const castedId = isNaN(queryObject.quoteProductId) ? queryObject.quoteProductId : parseInt(queryObject.quoteProductId);
      quoteProduct = new QuoteProductDto(this.props.quote.products.find((x) => x.id === castedId));
      this.loadDocuments(quoteProduct);
    } else {
      const defaultJson = this.getDefaultQuoteProductJson(quoteProductConfiguration);
      quoteProduct = new QuoteProductDto(defaultJson);
    }


    return quoteProduct;
  };

  getDefaultQuoteProductJson = (quoteProductConfiguration) => {
    const queryObject = this.getQueryObject();

    if (queryObject.addCoupler === 'true') {
      const attachmentPoint = quoteProductConfiguration.attachmentPoints.find(
        (x) => x.id === queryObject.attachmentPointId
      );
      const couplerGroupForAttachmentPoint =
        quoteProductConfiguration.productGroups.find(
          (x) =>
            x.isCoupler && x.attachmentPointId === queryObject.attachmentPointId
        );

      if (attachmentPoint && couplerGroupForAttachmentPoint) {
        const productCategoryIdsForCoupler = _.intersection(
          attachmentPoint.productCategoryIds,
          couplerGroupForAttachmentPoint.productCategoryIds
        );

        return {
          attachmentPointId: attachmentPoint.id,
          productCategoryId: _.first(productCategoryIdsForCoupler),
        };
      }
    }

    const inventoryQuoteAttachmentPoint = window.inventoryQuoteDataViewModel?.attachmentPointId
      ? { attachmentPointId: window.inventoryQuoteDataViewModel?.attachmentPointId }
      : null;

    return quoteProductConfiguration.isSingleAttachmentPointCarrier()
      ? { attachmentPointId: quoteProductConfiguration.attachmentPoints[0].id }
      : inventoryQuoteAttachmentPoint;
  };

  updateQuoteProduct = async (updateObject, getDocuments) => {
    const { quoteProductConfiguration, step1, quote } = this.props;
    const { quoteProductDto, forceResetLeadTimeOverrides } = this.state;

    const updated = new QuoteProductDto({
      ...quoteProductDto,
      ...updateObject,
    });

    if (step1.dealerBranchSummary) {
      // if we are in Price Lookup mode, we will NOT have a dealerBranchSummary available
      // in this situation, it isn't possible to have an override price, so we don't need to run the below code
      if (updated.productId !== quoteProductDto.productId) {
        updated.removeOverridePrice(
          step1.dealerBranchSummary.msrpMarkupPercentage
        );
      }
      updated.productOptions.forEach((updatedProductOption) => {
        var originalOption = quoteProductDto.productOptions.find(
          (x) =>
            x.productOptionSetId === updatedProductOption.productOptionSetId &&
            x.productOptionId === updatedProductOption.productOptionId
        );
        if (!originalOption) {
          updatedProductOption.removeOverridePrice(
            step1.dealerBranchSummary.msrpMarkupPercentage
          );
        }
      });
    }
    this.props.markSelectionInProgress(
      updated.isSelectionInProgress(
        quoteProductConfiguration.isSingleAttachmentPointCarrier()
      )
    );

    if (getDocuments) {
      if (updated.productId) {
        if (
          quoteProductDto.productId !== updated.productId ||
          JSON.stringify(quoteProductDto.productOptions) !==
          JSON.stringify(updated.productOptions)
        ) {
          this.loadDocuments(updated);
        }
      } else this.setState({ documents: null });
    }

    if (forceResetLeadTimeOverrides) {
      updated.leadTimeOverrides = [];
      this.setState({ leadTimeOverrides: false });
    }

    this.setState({ quoteProductDto: updated }, () => {
      this.refreshPrices();
      this.calculateQuoteProductLeadTime();
    });
  };

  calculateQuoteProductLeadTime = async () => {
    const { quoteProductDto } = this.state;
    const { quote, step1 } = this.props;
    if (quoteProductDto && quoteProductDto.productId) {
      this.setState({ showLeadTimeCalculationSpinner: true });
      const payload = {
        productId: quoteProductDto.productId,
        productGroupId: quoteProductDto.productGroupId,
        inventoryItemId: quoteProductDto.inventoryItemId,
        productOptionIds: quoteProductDto.productOptions.filter(x => x.productOptionId).map(x => x.productOptionId),
        weightCategoryId: step1.weightCategoryId,
        machineId: step1.machineId,
        attachmentPoint: quote.attachmentPoints.find(x => x.id === quoteProductDto.attachmentPointId),
        productDetail: quoteProductDto.detail,
        carrierId: step1.carrierId
      };

      var leadTime = await quoteProductLeadTimeService.calculateLeadTime(payload);
      if (leadTime) {
        quoteProductDto.leadTime = leadTime;
        this.setState({ quoteProductDto: quoteProductDto, showLeadTimeCalculationSpinner: false });
      }
    }
  }

  refreshPrices = async () => {
    const { quoteProductDto } = this.state;
    const { quote } = this.props;
    if (quoteProductDto && quoteProductDto.productId) {

      const productsAux = quote.products.map((x) =>
        x.id === quoteProductDto.id ? quoteProductDto : x
      );

      if (!productsAux.some(x => x.id === quoteProductDto.id)) {
        productsAux.push(quoteProductDto);
      }

      this.setState({ showPricingSpinner: true });
      const payload = {
        quoteId: quote.id,
        useLatestDiscounts: true,
        dealerBranchId: quote.step1.dealerBranchId,
        priceZoneId: quote.step1.priceZoneId,
        carrierId: quote.step1.carrierId,
        attachmentPoints: quote.attachmentPoints,
        products: [...productsAux],
        customLineItems: quote.customLineItems,
        optInDiscounts: quote.optInDiscounts
      };

      var calculateProducts = await quotePricingService
        .calculatePricing(payload)

      if (calculateProducts) {
        // we need to use a string equality comparison here because sometimes the local id could initially be a number, not a string
        const price = calculateProducts.products.find((x) => String(x.id) === String(quoteProductDto.id) || (x.id === undefined && quoteProductDto.id === null));
        if (price) {
          quoteProductDto.pricing = price.pricing;
        }
        this.setState({ quoteProductDto: quoteProductDto, showPricingSpinner: false });
      }
    }
  }

  loadDocuments = async (quoteProduct) => {
    const productOptionIds = quoteProduct.productOptions
      .filter((x) => !!x.productOptionId)
      .map((z) => z.productOptionId);
    this.setState({
      documents: await SharedDocument.sharedDocumentsCalculator(
        quoteProduct.productId,
        quoteProduct.productGroupId,
        productOptionIds
      ),
    });
  };

  saveProduct = () => {
    const { quoteProductDto, isEditProductMode } = this.state;
    const { id, inventoryItemId } = quoteProductDto;
    if (
      inventoryItemId &&
      inventoryItemId !== null &&
      this.props.quote.products.some(
        (x) => x.inventoryItemId === inventoryItemId && x.id !== id
      )
    ) {
      NotificationManager.error(
        'The selected stock item already exists on this quote, and cannot be added again. To increase the quantity, please go to the Review step.'
      );
    } else {
      isEditProductMode
        ? this.props.onUpdateProduct(quoteProductDto)
        : this.props.onAddProduct({
          quoteProductDto,
          onProductAdded: this.clearQuoteProduct,
        });
    }
  };

  clearQuoteProduct = () => {
    const attachmentPointId = this.state.quoteProductDto.attachmentPointId;
    const defaultQuoteProduct = new QuoteProductDto({ ...this.getDefaultQuoteProduct(this.props.quoteProductConfiguration), attachmentPointId });
    this.setState({ quoteProductDto: defaultQuoteProduct });
  };

  getQueryObject = () => {
    return queryString.parse(this.props.location.search);
  };

  onChangeQuoteProductType = (selectedQuoteProductType) => {
    this.setState({ selectedQuoteProductType: selectedQuoteProductType });
  }

  handleOnViewInventoryItem = (inventoryItemId) => {
    if (!inventoryItemId) {
      throw Error('Cannot edit Inventory Item. Invalid id specified.');
    }
    this.props.history.push(`/inventory-items/edit/${inventoryItemId}`);
  };

  onProductSelected = (productId, inventoryItemId) => {
    const { quoteProductDto } = this.state;
    if (productId && quoteProductDto.leadTime?.product?.correlationId && productId !== quoteProductDto.leadTime.product.correlationId) {
      this.setState({ forceResetLeadTimeOverrides: true });
    }
  };

  render() {
    const {
      submitting,
      productConfiguration,
      quoteProductConfiguration,
      onContinue,
      preparingForReview,
      quote,
      contact,
      mode,
      onloadSharedDocuments,
      onQuoteUpdated,
      order
    } = this.props;

    const {
      quoteProductDto,
      isEditProductMode,
      showCancelConfirmationModal,
      hasAgreedStockTerms,
      documents,
      showPricingSpinner,
      showLeadTimeCalculationSpinner,
      selectedQuoteProductType
    } = this.state;

    const attachmentPoint = quote.attachmentPoints.find((x) => quoteProductDto && quoteProductDto.attachmentPointId === x.id);
    const couplerParNumberMade = !!attachmentPoint && attachmentPoint.couplerDecision !== couplerDecision.notSpecified;

    if (!quoteProductConfiguration || !quoteProductDto) {
      return (
        <div className="container text-center">
          <h4 className="mt-5 mb-2">Loading</h4>
          <Spinner show={true} size="fa-5x" />
        </div>
      );
    }
    return (
      <div>
        <CreateQuoteStep3Component
          contact={contact}
          submitting={submitting}
          productConfiguration={productConfiguration}
          quoteProductConfiguration={quoteProductConfiguration}
          quoteProductDto={quoteProductDto}
          couplerParNumberMade={couplerParNumberMade}
          onQuoteProductDtoChange={this.updateQuoteProduct}
          onSaveProduct={this.saveProduct}
          onContinue={onContinue}
          onCancel={() => {
            if (mode === quoteWizardMode.amiPriceLookupMode) onContinue();
            else this.setState({ showCancelConfirmationModal: true });
          }}
          isEditProductMode={isEditProductMode}
          preparingForReview={preparingForReview}
          isOrderFormMode={mode === quoteWizardMode.orderFormMode}
          isAmiPriceLookupMode={mode === quoteWizardMode.amiPriceLookupMode}
          hasAgreedStockTerms={hasAgreedStockTerms}
          onHasAgreedStockTermsChange={(hasAgreed) =>
            this.setState({ hasAgreedStockTerms: hasAgreed })
          }
          quote={quote}
          documents={documents}
          onloadSharedDocuments={onloadSharedDocuments}
          showPricingSpinner={showPricingSpinner}
          showLeadTimeCalculationSpinner={showLeadTimeCalculationSpinner}
          onProductGroupSelectionChange={this.onChangeQuoteProductType}
          selectedQuoteProductType={selectedQuoteProductType}
          onQuoteUpdated={onQuoteUpdated}
          onOpenEdit={this.handleOnViewInventoryItem}
          order={order}
          productSelected={this.onProductSelected}
        />
        <CancelConfirmationModal
          title={`Cancel ${isEditProductMode ? 'Edit' : 'Add'} Product`}
          text={`Are you sure you would like to cancel ${isEditProductMode ? 'editing' : 'adding'
            } the current product?`}
          show={showCancelConfirmationModal}
          onShowUpdated={(showCancelConfirmationModal) => {
            this.setState({ showCancelConfirmationModal });
          }}
          onYes={() =>
            isEditProductMode ? onContinue() : this.clearQuoteProduct()
          }
        />
      </div>
    );
  }
}

const CreateQuoteStep3Container = withRouter(
  makeConcealable(CreateQuoteStep3ContainerComponent)
);
export { CreateQuoteStep3Container };
